import React from "react";
import styles from "./LastSessionCard.module.scss";
import { useGetLastSession } from "@/hooks/devourplay/useGetLastSession";
import moment from "moment";
import Skeleton from "@/components/skeletons/Skeleton";
import { IoGameControllerOutline } from "react-icons/io5";
import { FaRegClock } from "react-icons/fa";
import { RiVipDiamondLine } from "react-icons/ri";
import { BiTargetLock } from "react-icons/bi";
import { FiClock } from "react-icons/fi";
import useGetDevourPlayUserStats from "@/hooks/useGetDevourPlayUserStats";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";

export const LastSessionCard: React.FC = () => {
    const { data, isLoading, error } = useGetLastSession();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { data: userStats } = useGetDevourPlayUserStats(fullToken, undefined, !!fullToken);

    // Find the weekly time quest stat
    const weeklyTimeQuest = userStats?.userStats?.find(stat =>
        stat.weeklyProgress !== undefined && stat.rewardCategory === "TIME");
    const weeklyXP = weeklyTimeQuest?.weeklyProgress || 0;

    const formatDuration = (minutes: number) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}h ${mins}m`;
        }
        return `${mins}m`;
    };

    const formatTimeAgo = (timestamp: string) => {
    // Customize moment's relative time to use abbreviations
        const time = moment(timestamp).fromNow();
        return time
            .replace(" hours", " hr")
            .replace(" hour", " hr")
            .replace(" minutes", " min")
            .replace(" minute", " min")
            .replace(" months", " mo")
            .replace(" month", " mo")
            .replace(" days", " d")
            .replace(" day", " d")
            .replace(" years", " yr")
            .replace(" year", " yr")
            .replace("an ", "1 ")
            .replace("a ", "1 ");
    };

    if (isLoading) {
        return (
            <div className={styles.lastSessionCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <IoGameControllerOutline />
                        </span>
                        <h3>Gaming Sessions</h3>
                    </div>
                </div>
                <div className={styles.content}>
                    <Skeleton height={60} rows={1} />
                    <Skeleton height={20} width="60%" rows={1} classes={styles.skeletonMargin} />
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className={styles.lastSessionCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <IoGameControllerOutline />
                        </span>
                        <h3>Gaming Sessions</h3>
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.noSession}>
                        <div className={styles.emptyIcon}>🎮</div>
                        <h4>Ready to Start Gaming?</h4>
                        <p>Your gaming sessions and weekly time XP will appear here once you start playing.</p>
                    </div>
                </div>
            </div>
        );
    }

    const { lastSession, todaySummary, recentSessions } = data;

    if (!lastSession) {
        return (
            <div className={styles.lastSessionCard}>
                <div className={styles.header}>
                    <div className={styles.titleGroup}>
                        <span className={styles.iconWrapper}>
                            <IoGameControllerOutline />
                        </span>
                        <h3>Gaming Sessions</h3>
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.noSession}>
                        <div className={styles.emptyIcon}>🎮</div>
                        <h4>Ready to Start Gaming?</h4>
                        <p>Your gaming sessions and weekly time XP will appear here once you start playing.</p>
                    </div>
                </div>
            </div>
        );
    }

    const timeAgo = moment(lastSession.lastPlayed).fromNow();
    const sessionHours = Math.floor(lastSession.sessionDuration / 60);
    const sessionMinutes = lastSession.sessionDuration % 60;

    return (
        <div className={styles.lastSessionCard}>
            <div className={styles.header}>
                <div className={styles.titleGroup}>
                    <span className={styles.iconWrapper}>
                        <IoGameControllerOutline />
                    </span>
                    <h3>Gaming Sessions</h3>
                </div>
            </div>

            <div className={styles.content}>
                {/* Weekly Time XP - Simplified */}
                <div className={styles.weeklyXPSimple}>
                    <div className={styles.xpRow}>
                        <span className={styles.label}>Your Weekly XP:</span>
                        <span className={styles.value}>
                            {weeklyXP} XP
                        </span>
                    </div>
                    <div className={styles.xpLimit}>
                        <span className={styles.limitText}>Earn 10XP/hour | Max 250XP/week</span>
                    </div>
                </div>

                {/* Recent Sessions - Now the main list */}
                <div className={styles.recentSessions}>
                    <h5>Recent Sessions:</h5>
                    <div className={styles.sessionsTable}>
                        {/* Show last 5 sessions including the most recent */}
                        {recentSessions && recentSessions.length > 0
                            ? recentSessions.slice(0, 5).map((session, index) =>
                                <div key={session.sessionId || index} className={styles.sessionRow}>
                                    <div className={styles.gameInfo}>
                                        <IoGameControllerOutline className={styles.gameIcon} />
                                        <span className={styles.gameTitle}>{session.gameTitle}</span>
                                    </div>
                                    <div className={styles.sessionStats}>
                                        <span className={styles.duration}>
                                            <FiClock className={styles.clockIcon} />
                                            {formatDuration(session.duration)}
                                        </span>
                                        <span className={styles.timeAgo}>{formatTimeAgo(session.timestamp)}</span>
                                    </div>
                                </div>)
                            : lastSession &&
              <div className={styles.sessionRow}>
                  <div className={styles.gameInfo}>
                      <IoGameControllerOutline className={styles.gameIcon} />
                      <span className={styles.gameTitle}>{lastSession.gameTitle}</span>
                  </div>
                  <div className={styles.sessionStats}>
                      <span className={styles.duration}>
                          <FiClock className={styles.clockIcon} />
                          {formatDuration(lastSession.sessionDuration)}
                      </span>
                      <span className={styles.timeAgo}>{formatTimeAgo(lastSession.lastPlayed)}</span>
                  </div>
              </div>
                        }
                    </div>
                </div>

                {/* Today's Summary - Focus on play time */}
                {todaySummary && todaySummary.totalTime > 0 &&
          <div className={styles.todaySummary}>
              <h5>Today's Playtime</h5>
              <div className={styles.summaryStats}>
                  <div className={styles.stat}>
                      <span className={styles.value}>
                          {Math.floor(todaySummary.totalTime / 60)}h {todaySummary.totalTime % 60}m
                      </span>
                      <span className={styles.label}>Total</span>
                  </div>
                  <div className={styles.stat}>
                      <span className={styles.value}>{todaySummary.uniqueGames}</span>
                      <span className={styles.label}>Games</span>
                  </div>
                  {todaySummary.sessions &&
                <div className={styles.stat}>
                    <span className={styles.value}>{todaySummary.sessions}</span>
                    <span className={styles.label}>Sessions</span>
                </div>
                  }
              </div>
          </div>
                }
            </div>
        </div>
    );
};